// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ResponsiveAccordion from "@eisinc/App/Components/ResponsiveAccordion/ResponsiveAccordion";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    isPdp?: boolean;
    extendedStyles?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    breakQtyHeader: { alignX: "center" },
    breakPriceHeader: { alignX: "center" },
    savingsHeader: { alignX: "center" },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
};

const ProductQuantityBreakPricing: FC<OwnProps & HasProduct> = ({ isPdp, product, productInfo, extendedStyles }) => {
    const showPricing = useAppSelector(state => state.context.showPricing);
    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));
    // XNG-Change : add skeleton
    const isLoading = (product as XProductModel).isLoading;
    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Skeleton height="60px" />
            </StyledWrapper>
        );
    }
    const { pricing } = productInfo;
    // XNG-Change: add !isPdp
    if (
        !isPdp ||
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing
    ) {
        return null;
    }

    // <XNG-Changes>
    const breaksMaxIndex = pricing.unitRegularBreakPrices.length - 1;
    const unitOfMeasure =
        product.unitOfMeasures?.find(o => o.unitOfMeasure === pricing.unitOfMeasure)?.description ||
        product.unitOfMeasures?.find(o => o.unitOfMeasure === pricing.unitOfMeasure)?.unitOfMeasureDisplay;

    const pricingUnit = product?.properties?.pricingUnit;
    // </XNG-Changes>

    // XNG-Change: changes on structure
    return (
        <ResponsiveAccordion breakpoint="sm">
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.title}>{translate("Buy In Bulk & Save")}</Typography>
                <DataTable {...styles.table}>
                    <DataTableBody {...styles.tableBody}>
                        {pricing!.unitRegularBreakPrices!.map((breakPrice, index) => (
                            <DataTableRow
                                {...styles.tableRow}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                    {breakPrice.breakQty}
                                    {index < breaksMaxIndex
                                        ? `-${pricing!.unitRegularBreakPrices![index + 1].breakQty - 1}`
                                        : `+`}
                                </DataTableCell>
                                <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                    {breakPrice.breakPriceDisplay}
                                    {unitOfMeasure && (
                                        <>
                                            &nbsp;/&nbsp;{pricingUnit || ""}
                                            {unitOfMeasure}
                                        </>
                                    )}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </ResponsiveAccordion>
    );
};

export default withProduct(ProductQuantityBreakPricing);
